import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { signOutSuccess, authenticated } from 'store/slices/authSlice';
import store from '../store';
import { forceReconnect as wsReconnect, disconnect as wsDisconnect } from '../wsClient';

const getStoredToken = () => localStorage.getItem(AUTH_TOKEN);
const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN);
const setStoredToken = (token) => localStorage.setItem(AUTH_TOKEN, token);
const setStoredRefreshToken = (token) => localStorage.setItem(REFRESH_TOKEN, token);
const clearTokens = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
};

let isRefreshing = false;
let failedQueue = [];
let refreshTimer = null;

const REFRESH_AHEAD_MS = 5 * 60 * 1000; // 5 minutes before expiry
const CROSS_TAB_KEY = '__token_refresh_lock__';

const getTokenExpiry = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return payload.exp * 1000;
  } catch (e) {
    return null;
  }
};

const decodeUserId = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return payload.id;
  } catch (e) {
    return null;
  }
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const acquireCrossTabLock = () => {
  const now = Date.now();
  const lock = localStorage.getItem(CROSS_TAB_KEY);
  if (lock) {
    const lockTime = parseInt(lock, 10);
    if (now - lockTime < 15000) return false;
  }
  localStorage.setItem(CROSS_TAB_KEY, String(now));
  return true;
};

const releaseCrossTabLock = () => {
  localStorage.removeItem(CROSS_TAB_KEY);
};

const doRefresh = async () => {
  const refreshToken = getStoredRefreshToken();
  const currentToken = getStoredToken();

  if (!refreshToken || !currentToken) {
    throw new Error('No tokens available');
  }

  const userId = decodeUserId(currentToken);

  const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
    refreshToken,
    userId,
  }, {
    headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {},
    timeout: 10000,
  });

  const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data;

  setStoredToken(newToken);
  if (newRefreshToken) {
    setStoredRefreshToken(newRefreshToken);
  }

  store.dispatch(authenticated({ token: newToken, user }));
  scheduleTokenRefresh(newToken);

  try { wsReconnect(); } catch (e) { /* ignore */ }

  return newToken;
};

const proactiveTokenRefresh = async () => {
  if (isRefreshing) return;

  if (!acquireCrossTabLock()) {
    console.log('[TokenManager] Another tab is refreshing, skipping');
    const token = getStoredToken();
    if (token) scheduleTokenRefresh(token);
    return;
  }

  isRefreshing = true;
  console.log('[TokenManager] Proactively refreshing token...');

  try {
    await doRefresh();
    console.log('[TokenManager] Proactive refresh successful');
  } catch (error) {
    console.error('[TokenManager] Proactive refresh failed:', error.message);
  } finally {
    isRefreshing = false;
    releaseCrossTabLock();
  }
};

const scheduleTokenRefresh = (token) => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const expiry = getTokenExpiry(token);
  if (!expiry) return;

  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  const refreshTime = timeUntilExpiry - REFRESH_AHEAD_MS;

  if (refreshTime > 0) {
    console.log(`[TokenManager] Scheduling refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`);
    refreshTimer = setTimeout(proactiveTokenRefresh, refreshTime);
  } else if (timeUntilExpiry > 0) {
    console.log('[TokenManager] Token expiring soon, refreshing now');
    proactiveTokenRefresh();
  }
};

const enqueueFailedRequest = () => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });
};

const handleUnauthorized = async (originalRequest, axiosInstance) => {
  if (originalRequest._retry) return Promise.reject(new Error('Already retried'));

  const isLoginRequest = originalRequest.url?.includes('/auth/login');
  const isRefreshRequest = originalRequest.url?.includes('/auth/refresh-token');

  if (isLoginRequest) return Promise.reject(new Error('Login failed'));
  if (isRefreshRequest) {
    clearTokens();
    store.dispatch(signOutSuccess());
    return Promise.reject(new Error('Refresh failed'));
  }

  if (isRefreshing) {
    const newToken = await enqueueFailedRequest();
    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
    return axiosInstance(originalRequest);
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    clearTokens();
    wsDisconnect();
    store.dispatch(signOutSuccess());
    return Promise.reject(new Error('No refresh token'));
  }

  try {
    console.log('[TokenManager] Refreshing after 401...');
    const newToken = await doRefresh();
    console.log('[TokenManager] 401-refresh successful');
    processQueue(null, newToken);
    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    console.error('[TokenManager] 401-refresh failed:', refreshError.response?.data || refreshError.message);
    processQueue(refreshError, null);
    clearTokens();
    wsDisconnect();
    store.dispatch(signOutSuccess());
    setTimeout(() => {
      if (window.location.pathname !== '/auth/login') {
        window.location.href = '/auth/login';
      }
    }, 1000);
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

const initTokenRefresh = () => {
  const token = getStoredToken();
  if (token) {
    scheduleTokenRefresh(token);
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === AUTH_TOKEN && e.newValue) {
      console.log('[TokenManager] Token updated in another tab, rescheduling refresh');
      scheduleTokenRefresh(e.newValue);
    }
    if (e.key === AUTH_TOKEN && !e.newValue) {
      console.log('[TokenManager] Token cleared in another tab, signing out');
      store.dispatch(signOutSuccess());
    }
  });
}

setTimeout(initTokenRefresh, 1000);

export {
  getStoredToken,
  getStoredRefreshToken,
  setStoredToken,
  setStoredRefreshToken,
  clearTokens,
  scheduleTokenRefresh,
  handleUnauthorized,
  processQueue,
  isRefreshing,
};
